import { useState } from "react";
import { Share2 } from "lucide-react";
import { useLang } from "@/lib/i18n";

const SITE_URL = "https://alisalemps.com/";

/**
 * Mobile share button — shown in place of the desktop QR card on phones.
 * Uses the native `navigator.share` picker when the browser supports it
 * (Safari on iPhone, Chrome on Android). Falls back to writing the URL to
 * the clipboard with a tiny toast confirmation. User-initiated cancels do
 * NOT surface as errors.
 */
export default function ShareSiteButton() {
    const [toast, setToast] = useState(null);
    const { t, lang } = useLang();
    const SHARE_DATA = { title: t("siteName"), text: t("shareSiteText"), url: lang !== "ar" ? SITE_URL + lang + "/" : SITE_URL };

    const showToast = (msg) => {
        setToast(msg);
        window.clearTimeout(showToast.timer);
        showToast.timer = window.setTimeout(() => setToast(null), 2000);
    };

    const onClick = async () => {
        // Prefer the native share sheet.
        if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
            try {
                await navigator.share(SHARE_DATA);
                return;
            } catch (err) {
                // AbortError = user cancelled the sheet. Do NOT show an error.
                const name = err?.name || "";
                if (name === "AbortError" || name === "NotAllowedError") return;
                // Anything else falls through to the clipboard fallback below.
            }
        }
        // Fallback: copy URL to clipboard.
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(SHARE_DATA.url);
            } else {
                // Very old browsers — use a temporary textarea.
                const ta = document.createElement("textarea");
                ta.value = SHARE_DATA.url;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            showToast(t("siteLinkCopied"));
        } catch {
            // If copying also fails just silently do nothing — no scary alert.
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                data-testid="footer-share-btn"
                className="w-full max-w-sm inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-red-700 active:bg-red-800 text-white font-bold rounded-md px-6 transition-colors"
                style={{ height: "48px" }}
                aria-label={t("shareSite")}
            >
                <Share2 className="w-5 h-5" aria-hidden="true" />
                <span>{t("shareSite")}</span>
            </button>
            {toast && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed inset-x-0 bottom-6 flex justify-center pointer-events-none z-50"
                    data-testid="share-toast"
                >
                    <div className="bg-black/90 text-white text-sm px-4 py-2 rounded-md shadow-lg pointer-events-auto">
                        {toast}
                    </div>
                </div>
            )}
        </>
    );
}
